import { useCallback, useEffect, useRef, useState } from 'react';

import type { EndpointError } from '@core/types/types.endpoint';
import type { QuestionListEndpoint } from '@core/types/types.endpoint.question';
import { useBuster } from '@frontend/shared/hooks/useBuster';
import { callApi } from '@frontend/shared/util/util.callApi';

export function useQuestionList({
  condition = true,
}: { condition?: boolean } = {}) {
  const buster = useBuster('bust');
  const [result, setResult] = useState<QuestionListEndpoint['response'] | null>(
    null,
  );
  const [error, setError] = useState<EndpointError | null>(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const busy = useRef(false);

  const fetchPage = useCallback(async (cursor?: string) => {
    const version = generation.current;
    busy.current = true;
    setLoading(true);
    const [err, data] = await callApi<QuestionListEndpoint>('/question/list', {
      cursor,
      limit: 20,
    });
    if (version !== generation.current) return;
    if (data)
      setResult((previous) => ({
        ...data,
        questions: cursor
          ? [...(previous?.questions ?? []), ...data.questions].filter(
              (q, index, all) =>
                all.findIndex((other) => other.id === q.id) === index,
            )
          : data.questions,
      }));
    setError(err);
    setLoading(false);
    busy.current = false;
  }, []);

  useEffect(() => {
    const version = ++generation.current;
    busy.current = false;
    if (condition) {
      setResult(null);
      void fetchPage();
    }
    return () => {
      generation.current = version + 1;
    };
  }, [condition, buster, fetchPage]);

  const loadMore = () => {
    if (!busy.current && result?.nextCursor) void fetchPage(result.nextCursor);
  };
  return { res: 'browse' as const, loading, error, result, loadMore };
}
